import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from "react-router-dom";
import { useAppId } from "../../context/AppIdContext";
import '../../style/adminList.css';
import '../../style/speakSearch.css';
import Header from '../../component/header';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const PollingLocationVoters = () => {
    // --- Router & Context State ---
    const { appId, language } = useAppId();
    const location = useLocation();
    const navigate = useNavigate();

    // Extract ID from the navigation state (supports both user_id or polling_id based on previous page)
    const polling_location = location.state?.user_id || location.state?.polling_location;

    // --- Logic State ---
    const [voters, setVoters] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");

    // --- Pagination State ---
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 50;

    const lang = language || 'en';

    // Translation dictionary mapped for Polling Location
    const t = {
        en: {
            title: "Polling Location Voters",
            subtitle: "List of voters for this specific polling location.",
            searchPlaceholder: "Search by name or mobile...",
            noRecordsFound: "No voters found matching",
            loadingText: "Fetching Records...",
            fatherHusband: "F/S :",
            serialNo: "Sr No. :",
            mobile: "Mobile :",
            idCardNo: "ID Card No :",
            boothNo: "Booth No :",
            houseNo: "House No :",
            pollingLoc: "Voting Loc :",
            sentDate: "Sent On :",
            call: "Call",
            text: "Text",
            slip: "Slip",
            prachar: "Prachar",
            family: "Family",
            btnSearch: "Search",
            back: "Back to Dashboard",
            familyError: "Could not fetch family details.",
            print: "Print"
        },
        hi: {
            title: "मतदान स्थल मतदाता",
            subtitle: "इस विशिष्ट मतदान स्थल के मतदाताओं की सूची।",
            searchPlaceholder: "नाम या मोबाइल से खोजें...",
            noRecordsFound: "कोई मतदाता नहीं मिला",
            loadingText: "रिकॉर्ड प्राप्त कर रहे हैं...",
            fatherHusband: "पिता/पति :",
            serialNo: "क्रमांक :",
            mobile: "मोबाइल :",
            idCardNo: "पहचान पत्र नं.:",
            boothNo: "बूथ नं :",
            houseNo: "घर नं :",
            pollingLoc: "मतदान स्थान :",
            sentDate: "भेजा गया :",
            call: "कॉल",
            text: "मैसेज",
            slip: "पर्ची",
            prachar: "प्रचार",
            family: "परिवार",
            btnSearch: "खोजें",
            back: "डैशबोर्ड पर वापस जाएं",
            familyError: "परिवार का विवरण प्राप्त नहीं किया जा सका।",
            print: "प्रिन्ट"
        },
        gu: {
            title: "મતદાન સ્થળ મતદારો",
            subtitle: "આ ચોક્કસ મતદાન સ્થળ માટે મતદારોની યાદી.",
            searchPlaceholder: "નામ અથવા મોબાઇલ દ્વારા શોધો...",
            noRecordsFound: "કોઈ મતદાર મળ્યો નથી",
            loadingText: "રેકોર્ડ મેળવી રહ્યા છે...",
            fatherHusband: "પિતા/પતિ :",
            serialNo: "ક્રમાંક :",
            mobile: "મોબાઇલ :",
            idCardNo: "ઓળખ કાર્ડ નં :",
            boothNo: "બૂથ નં :",
            houseNo: "ઘર નં :",
            pollingLoc: "મતદાન સ્થળ :",
            sentDate: "મોકલ્યા તારીખ :",
            call: "કૉલ",
            text: "સંદેશ",
            slip: "સ્લિપ",
            prachar: "પ્રચાર",
            family: "કુટુંબ",
            btnSearch: "શોધો",
            back: "ડેશબોર્ડ પર પાછા જાઓ",
            familyError: "કુટુંબની વિગતો મેળવી શકાઈ નહીં.",
            print:"Print"
        }
    };

    const txt = t[lang] || t['en'];

    // --- API Fetching ---
    useEffect(() => {
        if (!appId) {
            navigate('/');
            return;
        }
         else {
            setLoading(false);
        }
    }, [appId]);

    const fetchVotersData = async () => {
        setLoading(true);
        setCurrentPage(1);
        try {
            const response = await axios.post(
                `${BASE_URL}/api/polling_location_wise_slip_sending_voter`,
                {
                    app_id: appId,
                    polling_location: polling_location   // Also passing as polling_id to be safe depending on API structure
                }
            );
            
            if (response.data.success && response.data.data && response.data.data.result) {
                setVoters(response.data.data.result);
            } else {
                setVoters([]);
            }
        } catch (err) {
            console.error("API Error:", err);
            setVoters([]);
        } finally {
            setLoading(false);
        }
    };

    // --- WhatsApp Message Generation Logic ---
    const handleWhatsAppShare = async (voter) => {
        if (!voter.contact_no) {
            alert("Mobile number not available.");
            return;
        }

        try {
            const response = await axios.post(`${BASE_URL}/api/get_receipt_in_all_family_for_wtsp`, {
                app_id: appId,
                voter_id: voter.id,
                user_id: appId
            });

            if (response.data.success && response.data.data && response.data.data.result) {
                const familyMembers = response.data.data.result;
                let message = "";

                familyMembers.forEach((member, index) => {
                    message += `${member.first_detail.replace(/\s+/g, ' ')}\n`;
                    message += `${member.full_name}\n`;
                    message += `${member.middle_name}\n`;
                    message += `${member.sex_age.replace('स्री', 'स्त्री')}\n`;
                    message += `${member.idcard_no}\n\n`;
                    message += `मतदान स्थल :\n`;
                    message += `${member.eng_polling_location}\n\n`;

                    if (index < familyMembers.length - 1) {
                        message += `----------------------------------------\n\n`;
                    }
                });

                const encodedMessage = encodeURIComponent(message);
                let mobile = voter.contact_no.replace(/\D/g, '');
                if (mobile.length === 10) mobile = '91' + mobile;
                window.open(`https://wa.me/${mobile}?text=${encodedMessage}`, '_blank');

            } else {
                alert(txt.familyError);
            }
        } catch (error) {
            console.error("Error generating WhatsApp slip:", error);
            alert(txt.familyError);
        }
    };

    // --- Filter Logic ---
    const filteredVoters = voters.filter(voter => {
        if (!searchTerm) return true;
        const lowerSearch = searchTerm.toLowerCase();

        const fullName = [voter.eng_f_name, voter.eng_m_name, voter.eng_surname].filter(Boolean).join(" ").toLowerCase();

        return (
            fullName.includes(lowerSearch) ||
            (voter.idcard_no && voter.idcard_no.toLowerCase().includes(lowerSearch)) ||
            (voter.contact_no && String(voter.contact_no).includes(lowerSearch))
        );
    });

    // --- Pagination Logic ---
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentVoters = filteredVoters.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredVoters.length / itemsPerPage);

    const paginate = (pageNumber) => {
        setCurrentPage(pageNumber);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const getPageNumbers = () => {
        const pageNumbers = [];
        const maxVisibleButtons = 5;
        if (totalPages <= maxVisibleButtons) {
            for (let i = 1; i <= totalPages; i++) pageNumbers.push(i);
        } else {
            pageNumbers.push(1);
            let startPage = Math.max(2, currentPage - 1);
            let endPage = Math.min(totalPages - 1, currentPage + 1);
            if (currentPage <= 3) endPage = 4;
            if (currentPage >= totalPages - 2) startPage = totalPages - 3;
            if (startPage > 2) pageNumbers.push('...');
            for (let i = startPage; i <= endPage; i++) pageNumbers.push(i);
            if (endPage < totalPages - 1) pageNumbers.push('...');
            pageNumbers.push(totalPages);
        }
        return pageNumbers;
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            // Trigger filter (already handled by real-time filter, but keeps UX consistent)
        }
    };

    return (
        <div className='app-container p-4'>

            <Header title={txt.title} />

            {polling_location && !loading && (
                <div className="mb-3 text-primary fw-bold" style={{fontSize:"smaller"}}>
                    Location: {polling_location}
                </div>
            )}

            {/* --- PROFESSIONAL FILTER CONTAINER --- */}
            <div className="filter-container mt-3">
                <div className="search-wrapper">
                    <svg className="search-icon-main" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <input
                        type="text"
                        className="search-input"
                        placeholder={txt.searchPlaceholder}
                        value={searchTerm}
                        onChange={(e) => {
                            setSearchTerm(e.target.value);
                            setCurrentPage(1); // Reset to first page on search
                        }}
                        onKeyDown={handleKeyDown}
                        style={{ paddingRight: '50px' }}
                    />
                </div>
            </div>

            {/* --- Loading State --- */}
            {loading && (
                <div className="loader-wrapper">
                    <div className="spinner"></div>
                    <p className="loader-text">{txt.loadingText}</p>
                </div>
            )}

            {/* --- Results Grid (SpeakAndSearch Style) --- */}
            <div className="row g-4 mt-1 pb-5">
                {!loading && currentVoters.length > 0 ? (
                    currentVoters.map((voter, index) => {
                        return (
                            <div className="col-12 col-md-6 col-lg-4 slide-up-element" key={voter.id || index} style={{ animationDelay: `${index * 0.05}s` }}>
                                <div className="voter-card-clean h-100">
                                    <div className="voter-header">
                                        <span className="serial-number">{indexOfFirstItem + index + 1}.</span>
                                        <span className="voter-name-bold ms-2">{voter.eng_f_name} {voter.eng_surname}</span>
                                    </div>
                                    <div className="voter-body position-relative">
                                        <div className="map-icon-wrapper">
                                            <img src="https://upload.wikimedia.org/wikipedia/commons/a/aa/Google_Maps_icon_%282020%29.svg" alt="Map" width="15" />
                                        </div>
                                        <div className="info-row"><span className="label">{txt.fatherHusband}</span><span className="value">{voter.eng_m_name || "N/A"}</span></div>
                                        <div className="info-row"><span className="label">{txt.serialNo}</span><span className="value">{voter.slnoinpart || "N/A"}</span></div>
                                        <div className="info-row"><span className="label">{txt.mobile}</span><span className="value mobile-value">{voter.contact_no || "N/A"}</span></div>
                                        <div className="info-row"><span className="label">{txt.idCardNo}</span><span className="value">{voter.idcard_no || "N/A"}</span></div>
                                        <div className="info-row"><span className="label">{txt.boothNo}</span><span className="value">{voter.part_no || "N/A"}</span></div>
                                        <div className="info-row"><span className="label">{txt.houseNo}</span><span className="value">{voter.eng_house_no || "N/A"}</span></div>
                                        <div className="info-row"><span className="label">{txt.pollingLoc}</span><span className="value">{voter.eng_polling_location || "-"}</span></div>

                                        {/* Sent Date info row */}
                                        <div className="info-row mt-2 pt-2" style={{ borderTop: '1px dashed #e2e8f0' }}>
                                            <span className="label">{txt.sentDate}</span>
                                            <span className="value text-success font-weight-bold">{voter.send_date || "-"}</span>
                                        </div>
                                    </div>
                                    <div className="voter-actions">
                                        <ActionButton icon="📞" color="#0ea5e9" label={txt.call} link={`tel:${voter.contact_no}`} />
                                        <ActionButton icon="✉️" color="#f59e0b" label={txt.text} link={`sms:${voter.contact_no}`} />
                                        <button onClick={() => handleWhatsAppShare(voter)} className="action-btn-wrapper" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                                            <div className="circle-btn" style={{ backgroundColor: '#22c55e' }}>💬</div>
                                            <span className="btn-label">{txt.slip}</span>
                                        </button>
                                        <ActionButton icon="👤" color="#ef4444" label={txt.prachar} />
                                        <ActionButton
                                            icon="🖨"
                                            color="#f97316"
                                            label={txt.print}
                                            onClick={() => navigate('/printer', { state: { voter_id: voter.id } })}
                                        />
                                        <ActionButton icon="👨‍👩‍👧" color="#fbbf24" label={txt.family} />
                                    </div>
                                </div>
                            </div>
                        );
                    })
                ) : (
                    !loading && (
                        <div className="col-12 text-center text-muted p-5">
                            {txt.noRecordsFound} "{searchTerm}"
                        </div>
                    )
                )}
            </div>

            {/* --- Pagination --- */}
            {!loading && filteredVoters.length > itemsPerPage && (
                <div className="pagination-wrapper">
                    <div className="pagination-content">
                        <button className="pagination-nav-btn" onClick={() => paginate(currentPage - 1)} disabled={currentPage === 1}>
                            <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
                        </button>
                        <div className="page-numbers">
                            {getPageNumbers().map((number, index) => (
                                <React.Fragment key={index}>
                                    {number === '...' ? (<span className="page-dots">...</span>) : (
                                        <button className={`page-num-btn ${currentPage === number ? 'active' : ''}`} onClick={() => paginate(number)}>{number}</button>
                                    )}
                                </React.Fragment>
                            ))}
                        </div>
                        <button className="pagination-nav-btn" onClick={() => paginate(currentPage + 1)} disabled={currentPage === totalPages}>
                            <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
                        </button>
                    </div>
                </div>
            )}

            <style jsx>{`
                .app-container { background: #E5E7EB; min-height: 100vh; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; }
                @keyframes slideInUp { from { opacity: 0; transform: translateY(15px); } to { opacity: 1; transform: translateY(0); } }
                .slide-up-element { opacity: 0; animation: slideInUp 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
                
                .filter-container { display: flex; justify-content: space-between; align-items: center; gap: 16px; margin-bottom: 24px; background: transparent; }
                .search-wrapper { position: relative; flex: 1; max-width: 100%; }
                .search-icon-main { position: absolute; left: 14px; top: 50%; transform: translateY(-50%); color: #94a3b8; width: 20px; height: 20px; z-index: 1; }
                .search-input { width: 100%; padding: 12px 60px 12px 42px; border: 1px solid #e2e8f0; border-radius: 10px; font-size: 14px; color: #1e293b; outline: none; transition: all 0.2s ease; box-shadow: 0 1px 2px rgba(0,0,0,0.05); }
                .search-input:focus { border-color: #2563eb; box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1); }
                
                .search-btn-icon { position: absolute; right: 10px; top: 50%; transform: translateY(-50%); background: #2563eb; border: none; border-radius: 8px; cursor: pointer; color: white; padding: 8px 12px; display: flex; align-items: center; justify-content: center; transition: 0.2s; z-index: 2; }
                .search-btn-icon:hover { background-color: #1d4ed8; }
                .search-btn-icon:disabled { background-color: #94a3b8; cursor: not-allowed; }
                .spinner-icon { animation: spin 1s linear infinite; }
                @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
                
                .loader-wrapper { display: flex; flex-direction: column; justify-content: center; align-items: center; min-height: 60vh; width: 100%; }
                .spinner { width: 40px; height: 40px; border: 4px solid #E5E7EB; border-top: 4px solid #2563EB; border-radius: 50%; animation: spin 1s linear infinite; margin-bottom: 12px; }
                .loader-text { color: #6B7280; font-weight: 500; }
                
                .text-success { color: #059669 !important; }
                .font-weight-bold { font-weight: 700 !important; }

                /* Pagination */
                .pagination-wrapper { display: flex; justify-content: center; padding: 20px 0; position: sticky; bottom: 20px; z-index: 50; }
                .pagination-content { background: white; border: 1px solid #E2E8F0; border-radius: 50px; padding: 6px 12px; display: flex; align-items: center; gap: 8px; box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1); }
                .pagination-nav-btn { display: flex; align-items: center; justify-content: center; width: 32px; height: 32px; border-radius: 50%; background: transparent; border: none; color: #2563EB; cursor: pointer; }
                .pagination-nav-btn:disabled { color: #CBD5E1; cursor: not-allowed; }
                .page-numbers { display: flex; gap: 4px; align-items: center; }
                .page-num-btn { width: 30px; height: 30px; border-radius: 50%; border: none; background: transparent; color: #475569; font-weight: 600; font-size: 13px; cursor: pointer; }
                .page-num-btn.active { background-color: #2563EB; color: white; }
                
                /* Mobile Optimization */
                @media (max-width: 640px) { 
                    .filter-container { width: 100%; }
                    .search-wrapper { width: 100%; max-width: 100%; }
                    .search-input { padding: 10px 50px 10px 36px; font-size: 13px; }
                    .search-icon-main { left: 10px; width: 16px; height: 16px; }
                    .search-btn-icon { right: 6px; padding: 6px 10px; }
                    .row.g-4 { margin-left: -8px; margin-right: -8px; }
                    [class*="col-"] { padding-left: 8px; padding-right: 8px; }
                }
            `}</style>
        </div>
    );
};

// Reusable ActionButton
const ActionButton = ({ icon, color, label, link, onClick }) => {
    if (onClick) {
        return (
            <button onClick={onClick} className="action-btn-wrapper" style={{ textDecoration: 'none', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                <div className="circle-btn" style={{ backgroundColor: color }}>{icon}</div>
                <span className="btn-label">{label}</span>
            </button>
        );
    }
    
    return (
        <a href={link || "#"} className="action-btn-wrapper" style={{ textDecoration: 'none' }} target="_blank" rel="noreferrer">
            <div className="circle-btn" style={{ backgroundColor: color }}>{icon}</div>
            <span className="btn-label">{label}</span>
        </a>
    );
};

export default PollingLocationVoters;